import { ReviewModalItem } from "@/components/display/ReviewModalItem";
import { getBaseThemeName } from "@/features/branding";
import { useGetToolUsageQuery } from "@/store/services/supabase";
import { GridItem } from "@/tools/aggregate/common/AnalysisCollectionFormItems";
import { PortfolioRegionType } from "@/tools/aggregate/portfolio-export/types";
import { ArrowUpOutlined } from "@ant-design/icons";
import { BoxArrowUpIcon } from "@phosphor-icons/react/dist/ssr";
import { Alert, Button, Modal, Progress, Statistic } from "antd";
import Link from "next/link";
import { useTranslation } from "react-i18next";
interface UpdateAvailableModalProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLoading?: boolean;
  confirmText?: string;
  error?: string | null;
  portfolioType?: PortfolioRegionType;
}

/**
 * Automated Resilience re-runs are free so the deduction is always zero and the bar matches current usage.
 */
const QuotaUsage = ({
  portfolioType
}: {
  portfolioType?: PortfolioRegionType;
}) => {
  const {
    data: toolUsage
  } = useGetToolUsageQuery({
    activeQuotaOnly: true
  });
  if (!toolUsage) return null;
  const usage = portfolioType === "global" && getBaseThemeName() === "hkma" ? toolUsage.portfolioInsightsGlobal : toolUsage.singleQuotaMode && toolUsage.singleQuotaUsage ? toolUsage.singleQuotaUsage : toolUsage.portfolioInsights;
  if (!usage || typeof usage.quota !== "number" || usage.quota === 0) return null;
  const deduction = 0;
  const usagePct = parseFloat((usage.usage / usage.quota * 100).toFixed(2));
  const usageStat = `${usage.usage.toLocaleString()} / ${usage.quota.toLocaleString()} assets (${usagePct}%)`;
  const deductionStat = `${deduction.toLocaleString()} (0%)`;
  return <ReviewModalItem title="Usage Confirmation" data-sentry-element="ReviewModalItem" data-sentry-component="QuotaUsage" data-sentry-source-file="UpdateAvailableModal.tsx">
      <GridItem className="!items-start" data-sentry-element="GridItem" data-sentry-source-file="UpdateAvailableModal.tsx">
        <div>Usage after re-processing</div>

        <div className="col-span-2 flex w-full flex-row justify-between gap-1 pr-2">
          <div className="col-span-2 flex flex-col gap-1">
            <Statistic value={usageStat} className="*:!text-sm" data-sentry-element="Statistic" data-sentry-source-file="UpdateAvailableModal.tsx" />
            <Statistic value={deductionStat} className="*:!text-sm *:!text-gray-400" suffix={<ArrowUpOutlined />} data-sentry-element="Statistic" data-sentry-source-file="UpdateAvailableModal.tsx" />
          </div>
          <Progress type="circle" percent={usagePct} strokeWidth={16} size={42} success={{
          percent: usage.usagePercent,
          strokeColor: "#cdcdcd"
        }} strokeColor={"var(--color-accent)"} showInfo={false} style={{
          gridColumn: "2 / 4"
        }} className="max-w-8" data-sentry-element="Progress" data-sentry-source-file="UpdateAvailableModal.tsx" />
        </div>
        <div className="col-span-2 col-start-2 flex flex-col gap-1">
          <p>No additional quota deducted for re-processing.</p>
        </div>
      </GridItem>
    </ReviewModalItem>;
};

/**
 * Confirmation for re-processing an analysis to pick up Automated Resilience.
 * Shared by the analysis list row (runs immediately) and the collection page
 * (looks up the list page, then redirects).
 */
export const UpdateAvailableModal = ({
  open,
  onConfirm,
  onCancel,
  confirmLoading = false,
  confirmText = "Re-process",
  error = null,
  portfolioType
}: UpdateAvailableModalProps) => {
  const {
    t
  } = useTranslation("terms");
  return <Modal open={open} onOk={onConfirm} onCancel={onCancel} okText={confirmText} cancelText="Cancel" confirmLoading={confirmLoading} cancelButtonProps={{
    disabled: confirmLoading
  }} maskClosable={!confirmLoading} keyboard={!confirmLoading} closable={!confirmLoading} width={660} title={<div className="flex items-center gap-2">
          <BoxArrowUpIcon size={32} weight="bold" style={{
      color: "var(--color-accent)",
      fill: "var(--color-accent)"
    }} />
          <span>Update Available</span>
        </div>} data-sentry-element="Modal" data-sentry-component="UpdateAvailableModal" data-sentry-source-file="UpdateAvailableModal.tsx">
      <div className="pt-2">
        <p className="mb-2">
          The Hub platform has received a feature update and new data is available for this portfolio. <br />
          Re-run the analysis to regenerate results and unlock these additional insights.
        </p>
        <Alert className="mb-2 border-none bg-subtle px-3 py-2" description={<div className="flex items-center gap-1">
              <div className="flex-1">
                <span style={{
            color: "var(--color-accent)"
          }} className="font-semibold text-gray-900">
                  NEW:
                </span>{" "}
                {t("automatedResilience")}
              </div>
              <Link className="hover:!text-gray-700" href={`/method/automated-resilience`}>
                <Button>Learn More</Button>
              </Link>
            </div>} data-sentry-element="Alert" data-sentry-source-file="UpdateAvailableModal.tsx" />
        <div>
          <span className="font-semibold">Please note:</span> <br />
          <ul className="mb-2 list-inside list-disc">
            <li>Reprocessing times may vary depending on the volume of points in the analysis.</li>
            <li>Existing results will be unavailable until the reprocessing is complete.</li>
          </ul>
        </div>

        <QuotaUsage portfolioType={portfolioType} data-sentry-element="QuotaUsage" data-sentry-source-file="UpdateAvailableModal.tsx" />

        {error && <Alert className="mt-2" type="error" showIcon description={error} />}
      </div>
    </Modal>;
};